/**
 * TOSS Web Application - Spinner Component
 *
 * Loading spinner component with multiple sizes and color variants.
 * Uses CSS animations for smooth, performant rotation.
 *
 * Sizes:
 * - xs: Extra small (w-3 h-3) - for inline/button use
 * - sm: Small (w-4 h-4) - for buttons
 * - md: Medium (w-6 h-6) - default
 * - lg: Large (w-8 h-8) - for page/section loading
 * - xl: Extra large (w-12 h-12) - for full page loading
 */

import type { HTMLAttributes } from 'react';
import { APPLICATION_CARD_GRID_COLS } from './cardGrids';

// ============================================================================
// Types
// ============================================================================

export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface SpinnerProps extends HTMLAttributes<HTMLDivElement> {
  /** Size of the spinner */
  size?: SpinnerSize;
  /** Color - uses currentColor by default for flexibility */
  color?: 'primary' | 'white' | 'gray' | 'current';
  /** Label for accessibility */
  label?: string;
  /** Show spinner centered in a container */
  centered?: boolean;
}

// ============================================================================
// Styles
// ============================================================================

const sizeStyles: Record<SpinnerSize, string> = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
  xl: 'w-12 h-12',
};

const colorStyles: Record<string, string> = {
  primary: 'text-primary-600',
  white: 'text-white',
  gray: 'text-[var(--color-text-disabled)]',
  current: 'text-current',
};

const borderWidthBySize: Record<SpinnerSize, string> = {
  xs: 'border-[1.5px]',
  sm: 'border-2',
  md: 'border-2',
  lg: 'border-[3px]',
  xl: 'border-4',
};

// ============================================================================
// Component
// ============================================================================

/**
 * Spinner component for loading states
 *
 * @example
 * ```tsx
 * // Basic spinner
 * <Spinner />
 *
 * // In a button
 * <Button isLoading>
 *   <Spinner size="sm" color="current" />
 *   Loading...
 * </Button>
 *
 * // Centered in container
 * <Spinner size="lg" centered />
 *
 * // With custom label
 * <Spinner label="Loading data..." />
 * ```
 */
export function Spinner({
  size = 'md',
  color = 'current',
  label = 'Loading...',
  centered = false,
  className = '',
  ...props
}: SpinnerProps): JSX.Element {
  const spinnerClasses = [
    'inline-block rounded-full',
    'border-solid border-current border-t-transparent',
    'animate-spin',
    sizeStyles[size],
    colorStyles[color],
    borderWidthBySize[size],
    className,
  ].join(' ');

  const spinner = (
    <div
      className={spinnerClasses}
      role="status"
      aria-label={label}
      {...props}
    >
      <span className="sr-only">{label}</span>
    </div>
  );

  if (centered) {
    return (
      <div className="flex items-center justify-center w-full h-full min-h-[100px]">
        {spinner}
      </div>
    );
  }

  return spinner;
}

// ============================================================================
// Loading Overlay
// ============================================================================

export interface LoadingOverlayProps {
  /** Whether the overlay is visible */
  isLoading: boolean;
  /** Loading text to display */
  text?: string;
  /** Make overlay cover full screen */
  fullScreen?: boolean;
  /** Spinner size */
  spinnerSize?: SpinnerSize;
  /** Additional class names */
  className?: string;
}

/**
 * Loading overlay that covers its parent container
 *
 * @example
 * ```tsx
 * <div className="relative">
 *   <LoadingOverlay isLoading={isLoading} text="Saving..." />
 *   <Content />
 * </div>
 * ```
 */
export function LoadingOverlay({
  isLoading,
  text,
  fullScreen = false,
  spinnerSize = 'lg',
  className = '',
}: LoadingOverlayProps): JSX.Element | null {
  if (!isLoading) {
    return null;
  }

  const overlayClasses = [
    'flex flex-col items-center justify-center gap-3',
    'bg-white/80 backdrop-blur-sm',
    'z-50',
    fullScreen ? 'fixed inset-0' : 'absolute inset-0',
    className,
  ].join(' ');

  return (
    <div className={overlayClasses} role="alert" aria-busy="true">
      <Spinner size={spinnerSize} color="primary" />
      {text && (
        <p className="text-sm text-[var(--color-text-secondary)] font-medium">{text}</p>
      )}
    </div>
  );
}

// ============================================================================
// Page Loading
// ============================================================================

export interface PageLoadingProps {
  /** Loading text to display */
  text?: string;
  /** Loading text to display (alias for text) */
  message?: string;
}

/**
 * Full page loading indicator
 *
 * @example
 * ```tsx
 * if (isLoading) {
 *   return <PageLoading text="Loading application..." />;
 * }
 * ```
 */
export function PageLoading({
  text,
  message,
}: PageLoadingProps): JSX.Element {
  // Support both text and message (alias)
  const loadingText = text ?? message ?? 'Loading...';
  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4 bg-[var(--color-bg-secondary)]">
      <Spinner size="xl" color="primary" />
      <p className="text-[var(--color-text-secondary)] text-lg font-medium">{loadingText}</p>
    </div>
  );
}

// ============================================================================
// Skeleton Loading
// ============================================================================

export interface SkeletonProps {
  /** Width of the skeleton */
  width?: string | number;
  /** Height of the skeleton */
  height?: string | number;
  /** Make it circular */
  circle?: boolean;
  /** Additional class names */
  className?: string;
}

/**
 * Skeleton loading placeholder
 *
 * @example
 * ```tsx
 * // Text line
 * <Skeleton width="60%" height={20} />
 *
 * // Avatar
 * <Skeleton circle width={40} height={40} />
 *
 * // Card
 * <Skeleton width="100%" height={200} className="rounded-lg" />
 * ```
 */
export function Skeleton({
  width,
  height,
  circle = false,
  className = '',
}: SkeletonProps): JSX.Element {
  const style = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  const skeletonClasses = [
    'animate-pulse-slow bg-[var(--color-border)]',
    circle ? 'rounded-full' : 'rounded',
    className,
  ].join(' ');

  return <div className={skeletonClasses} style={style} />;
}

// ============================================================================
// Skeleton Presets
// ============================================================================

export interface SkeletonCardProps {
  /** Number of content lines to show */
  lines?: number;
  /** Additional class names */
  className?: string;
}

/**
 * Card-shaped skeleton with header line + content lines
 *
 * @example
 * ```tsx
 * <SkeletonCard />
 * <SkeletonCard lines={4} />
 * ```
 */
export function SkeletonCard({
  lines = 3,
  className = '',
}: SkeletonCardProps): JSX.Element {
  return (
    // Тот же `app-surface`, что у карточки, которая её сменит: заглушка, оформленная иначе,
    // печатает прыжок ровно на месте загрузки.
    <div className={`app-surface p-4 lg:p-5 space-y-3 ${className}`}>
      {/* Header line */}
      <div className="animate-pulse-slow bg-[var(--color-border)] rounded h-5 w-2/5" />
      {/* Content lines */}
      {Array.from({ length: lines }, (_, i) => (
        <div
          key={i}
          className="animate-pulse-slow bg-[var(--color-border)] rounded h-4"
          style={{ width: i === lines - 1 ? '60%' : '100%' }}
        />
      ))}
    </div>
  );
}

export interface SkeletonTableRowProps {
  /** Number of columns */
  columns?: number;
  /** Additional class names */
  className?: string;
}

/**
 * Table row skeleton with configurable columns
 *
 * @example
 * ```tsx
 * <table>
 *   <tbody>
 *     <SkeletonTableRow columns={5} />
 *     <SkeletonTableRow columns={5} />
 *   </tbody>
 * </table>
 * ```
 */
export function SkeletonTableRow({
  columns = 5,
  className = '',
}: SkeletonTableRowProps): JSX.Element {
  return (
    <tr className={className}>
      {Array.from({ length: columns }, (_, i) => (
        <td key={i} className="px-4 py-3">
          <div
            className="animate-pulse-slow bg-[var(--color-border)] rounded h-4"
            style={{ width: i === 0 ? '70%' : i === columns - 1 ? '50%' : '85%' }}
          />
        </td>
      ))}
    </tr>
  );
}

export interface SkeletonAvatarProps {
  /** Size of the avatar in pixels */
  size?: number;
  /** Additional class names */
  className?: string;
}

/**
 * Circular skeleton for avatar placeholders
 *
 * @example
 * ```tsx
 * <SkeletonAvatar />
 * <SkeletonAvatar size={48} />
 * ```
 */
export function SkeletonAvatar({
  size = 40,
  className = '',
}: SkeletonAvatarProps): JSX.Element {
  return (
    <div
      className={`animate-pulse-slow bg-[var(--color-border)] rounded-full shrink-0 ${className}`}
      style={{ width: size, height: size }}
    />
  );
}

// ============================================================================
// SkeletonTable – full table skeleton wrapper
// ============================================================================

export interface SkeletonTableProps {
  /** Number of skeleton rows */
  rows?: number;
  /** Number of columns */
  cols?: number;
  /** Additional class names */
  className?: string;
}

/**
 * Full table skeleton: thead placeholder + N skeleton rows.
 *
 * @example
 * ```tsx
 * {isLoading && !data ? <SkeletonTable rows={8} cols={5} /> : <ActualTable ... />}
 * ```
 */
export function SkeletonTable({
  rows = 6,
  cols = 5,
  className = '',
}: SkeletonTableProps): JSX.Element {
  return (
    <div className={`app-surface overflow-x-auto ${className}`}>
      <table className="min-w-full divide-y divide-[var(--color-border)]">
        {/* Header */}
        <thead>
          <tr>
            {Array.from({ length: cols }, (_, i) => (
              <th key={i} className="px-4 py-3">
                <div className="animate-pulse-slow bg-[var(--color-border)] rounded h-4 w-16" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--color-border)]">
          {Array.from({ length: rows }, (_, i) => (
            <SkeletonTableRow key={i} columns={cols} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// SkeletonCardList – grid of skeleton cards
// ============================================================================

export interface SkeletonCardListProps {
  /** Number of skeleton cards to render */
  count?: number;
  /**
   * Grid-columns class. Defaults to the product's card grid (`cardGrids`), so a skeleton that
   * is not told anything already counts its columns the way the real grids do.
   *
   * Pass the SAME constant the page's real grid uses. A skeleton whose column count differs
   * from the list it stands in makes the page jump the moment data arrives.
   */
  gridCols?: string;
  /** Lines per card */
  linesPerCard?: number;
  /** Additional class names */
  className?: string;
}

/**
 * Grid of SkeletonCard components — matches card list page layouts.
 *
 * @example
 * ```tsx
 * {isLoading && !data ? (
 *   <SkeletonCardList count={12} gridCols={APPLICATION_CARD_GRID_COLS} />
 * ) : (
 *   <div className={APPLICATION_CARD_GRID}>
 *     {data.items.map(item => <ItemCard key={item.id} item={item} />)}
 *   </div>
 * )}
 * ```
 */
export function SkeletonCardList({
  count = 6,
  gridCols = APPLICATION_CARD_GRID_COLS,
  linesPerCard = 3,
  className = '',
}: SkeletonCardListProps): JSX.Element {
  return (
    <div className={`grid ${gridCols} gap-4 ${className}`}>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonCard key={i} lines={linesPerCard} />
      ))}
    </div>
  );
}

export default Spinner;
